import React, { useCallback, useEffect, useRef, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useSyncPimsIntegrationModelMutation,
  useGetClinicPimsIntegrationCapabilitySyncQuery,
  PimsIntegrationCapability,
  ClinicPimsIntegrationType,
} from 'shared/types/graphql';
import { format } from 'date-fns';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useTracking } from 'shared/providers/TrackingProvider';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { GeneralEvents } from 'shared/providers/TrackingProvider/EventNames';

export enum ErrorMessageType {
  Inline,
  Toast,
}

interface IPimsSync {
  style?: Record<string, unknown>;
  triggerSearch: () => void;
  shouldSync: boolean;
  resetSyncState: () => void;
  parentComponent: string;
  syncModels: PimsIntegrationCapability[];
  shouldShowLastSync?: boolean;
  errorType?: ErrorMessageType;
  showSuccessMessage?: boolean;
  className?: string;
  message?: string;
}

const PimsSync = ({
  style,
  triggerSearch,
  shouldSync,
  resetSyncState,
  parentComponent,
  syncModels,
  shouldShowLastSync = true,
  errorType = ErrorMessageType.Inline,
  showSuccessMessage = false,
  message,
  className,
}: IPimsSync): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { trackEvent } = useTracking();
  const [updated, setUpdatedState] = useState('');
  const [isSyncing, setSyncState] = useState(false);
  const [showError, setErrorState] = useState(false);
  const toast = useToast();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const errorMessage = 'Please wait at least 1 minute before refreshing again.';

  const formatDate = (date: string): string => {
    return format(new Date(date), "PP 'at' p");
  };

  useGetClinicPimsIntegrationCapabilitySyncQuery({
    variables: {
      where: {
        capability: {
          in: syncModels,
        },
        integration: {
          clinicId: {
            equals: currentClinicId,
          },
          type: {
            not: {
              equals: ClinicPimsIntegrationType.Bitwerx,
            },
          },
        },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    skip: !currentClinicId,
    onCompleted: (data) => {
      if (data?.findManyClinicPimsIntegrationCapability?.length) {
        const latestDate = data.findManyClinicPimsIntegrationCapability.reduce((a, b) =>
          a.lastSyncedAt > b.lastSyncedAt ? a : b,
        );
        setUpdatedState(formatDate(latestDate.lastSyncedAt));
      }
    },
  });

  const [syncPimsIntegrationModel] = useSyncPimsIntegrationModelMutation({
    variables: {
      data: {
        clinicId: currentClinicId || '',
        models: syncModels,
      },
    },
    onCompleted: (data) => {
      timerRef.current = setTimeout(() => {
        if (data?.syncManyPimsIntegrationModel?.length) {
          const latestDate = data.syncManyPimsIntegrationModel.reduce((a, b) =>
            a.lastSyncedAt > b.lastSyncedAt ? a : b,
          );
          setUpdatedState(formatDate(latestDate.lastSyncedAt));
          triggerSearch();
        }
        setSyncState(false);

        if (showSuccessMessage) {
          toast({
            status: 'success',
            title: message ?? 'Appointments refreshed',
          });
        }
      }, 2000);
    },
    onError: (error) => {
      const { message } = error;
      setSyncState(false);

      if (message.includes('please wait')) {
        if (errorType === ErrorMessageType.Toast) {
          toast({
            status: 'error',
            title: errorMessage,
          });
        } else {
          setErrorState(true);
        }
      } else {
        toast({
          status: 'error',
          title: message,
        });
      }
    },
  });

  const handleSync = useCallback((): void => {
    trackEvent(GeneralEvents.PimsSyncRefresh, { source: parentComponent });
    setSyncState(true);
    setErrorState(false);
    if (!!currentClinicId) {
      syncPimsIntegrationModel();
    }
  }, [currentClinicId, parentComponent, syncPimsIntegrationModel, trackEvent]);

  useEffect(() => {
    return (): void => clearTimeout(timerRef.current as NodeJS.Timeout);
  }, []);

  useEffect(() => {
    if (shouldSync) {
      resetSyncState();
      handleSync();
    }
  }, [handleSync, resetSyncState, shouldSync]);

  return (
    <VStack className={className || 'PimsSync__Container'} {...style}>
      <HStack align="center" justify="space-between" w="100%">
        {shouldShowLastSync && (
          <VStack align="flex-start" spacing={0}>
            <Text as="span" size="sm">
              Last updated:
            </Text>
            <Text as="span" size="sm">
              {updated}
            </Text>
          </VStack>
        )}
        <Button
          iconName="doubleArrowCircle"
          variant="ghost"
          size="sm"
          onClick={handleSync}
          loadingText="Refreshing"
          isLoading={isSyncing}
        >
          Refresh
        </Button>
      </HStack>
      {showError && <Text variant="danger">{errorMessage}</Text>}
    </VStack>
  );
};

export default PimsSync;
